import { useCommercialAgreements } from '@hooks/queries/commercialAgreements/useCommercialAgreements';
import React, { useState } from 'react';
import { v4 } from 'uuid';
import { Close } from '@mui/icons-material';
import {
  AppBar,
  Box,
  Dialog,
  Divider,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import { AsyncButton } from '../../components/Button/AsyncButton';
import DynamicListInput from '../../components/Form/DynamicListInput';
import TextInput from '../../components/Form/TextInput';
import { useParties } from '../../contexts/PartiesContext';
import { isInteger } from '../../utils/isInteger';
import { isPositiveDecimal } from '../../utils/isPostiveDecimal';
import { isValidFee } from '../../utils/validateFees';
import { NoCommercialAgreement } from './NoCommercialAgreement';
import { PaidCredentialForm } from './PaidCredentialForm';
import { IClaim, ICredPayload, PaidFieldConfig } from './types';
import { useHandleFree } from './useHandleFree';
import { useHandlePaid } from './useHandlePaid';

interface OfferCredentialProps {
  open: boolean;
  onClose: () => void;
}
const emptyPayload: ICredPayload = { holder: '', id: '', description: '' };
const emptyClaims = [{ id: v4(), subject: '', property: '', value: '' }];
export const OfferCredential: React.FC<OfferCredentialProps> = ({ open, onClose }) => {
  const [payload, setPayload] = React.useState<ICredPayload>(emptyPayload);
  const { party } = useParties();
  const [index, setIndex] = useState(0);
  const isFree = index === 0;
  const hasCommercialAgreements = !!useCommercialAgreements()?.data?.length;

  const showCommercialAgreementsWarning = !isFree && !hasCommercialAgreements;
  const [claims, setClaims] = useState<IClaim[]>(emptyClaims);

  const reset = () => {
    setPayload(emptyPayload);
    setClaims(emptyClaims);
    setIndex(0);
  };
  const handlePaid = useHandlePaid();
  const handleFree = useHandleFree();
  const meetsMinimumFee =
    !!payload.billingPeriodMinutes &&
    !!payload.feePerDayUsd &&
    isValidFee({
      billingPeriodMinutes: payload.billingPeriodMinutes,
      feePerDayUsd: payload.feePerDayUsd,
    });
  const disableFreeSubmit =
    !payload.holder ||
    !payload.id ||
    !payload.description ||
    !claims.length ||
    claims.some(({ subject, property, value }) => !subject || !property || !value);
  const disablePaidSubmit =
    disableFreeSubmit ||
    payload.holder === party ||
    !payload.billingPeriodMinutes ||
    !payload.depositInitialAmountUsd ||
    !payload.depositTargetAmountUsd ||
    !meetsMinimumFee ||
    !hasCommercialAgreements;
  const disableSubmit = isFree ? disableFreeSubmit : disablePaidSubmit;
  const feeErrorMessage =
    'Either Fee per day, or Billing period is too small. Please adjust either to a larger value.';
  const buttonLabel = isFree ? 'Offer free credential' : 'Offer paid credential';
  const holderError =
    !isFree && party === payload.holder ? 'Holder party ID cannot be your own.' : undefined;
  const displayFeeError =
    !!payload?.feePerDayUsd && !!payload?.billingPeriodMinutes && !meetsMinimumFee;
  const commonFields: { name: keyof ICredPayload; helperText?: string }[] = [
    {
      name: 'holder',
      helperText: holderError,
    },
    { name: 'id' },
    { name: 'description' },
  ];
  const paidFields: PaidFieldConfig[] = [
    { label: 'Fee per day', name: 'feePerDayUsd', adornment: 'USD', type: 'decimal' },
    {
      name: 'billingPeriodMinutes',
      label: 'Billing Period',
      adornment: 'Minutes',
      helperText: displayFeeError ? feeErrorMessage : undefined,
      type: 'integer',
    },
    {
      label: 'Credential Fees Reserve Amount',
      name: 'depositInitialAmountUsd',
      adornment: 'USD',
      type: 'decimal',
    },
    {
      label: 'Credential Fees Target Reserve Amount',
      name: 'depositTargetAmountUsd',
      adornment: 'USD',
      type: 'decimal',
    },
  ];
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    if (newValue === 0) {
      setPayload((p) => ({
        ...p,
        feePerDay: '',
        billingPeriodMinutes: '',
        depositInitialAmountUsd: '',
        depositTargetAmountUsd: '',
      }));
    }
    setIndex(newValue);
  };

  React.useEffect(() => {
    reset();
  }, [open]);

  const onFreeCredentialFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPayload((p) => ({ ...p, [name]: value }));
  };
  const onPaidCredentialFormChange = (e: React.ChangeEvent<HTMLInputElement>, type: string) => {
    const { name, value } = e.target;
    if (value === '') {
      setPayload((p) => ({ ...p, [name]: value }));
    }
    if (type === 'integer' && isInteger(value)) {
      setPayload((p) => ({ ...p, [name]: value }));
    }
    if (type === 'decimal' && isPositiveDecimal(value)) {
      setPayload((p) => ({ ...p, [name]: value }));
    }
  };
  const onSubmit = async () => {
    if (isFree) {
      await handleFree(payload, claims);
    } else {
      await handlePaid(payload, claims);
    }
    onClose();
  };

  const createNewClaim = (): IClaim => ({
    id: v4(),
    subject: '',
    property: '',
    value: '',
  });

  const renderClaim = (claim: IClaim, updateClaim: (updatedClaim: IClaim) => void) => (
    <Box>
      <TextInput
        label="Subject"
        value={claim.subject}
        setValue={(value) => updateClaim({ ...claim, subject: value as string })}
        required
      />
      <TextInput
        label="Property"
        value={claim.property}
        setValue={(value) => updateClaim({ ...claim, property: value as string })}
        required
      />
      <TextInput
        label="Value"
        value={claim.value}
        setValue={(value) => updateClaim({ ...claim, value: value as string })}
        required
      />
    </Box>
  );
  return (
    <Dialog open={open} onClose={onClose}>
      <Box
        sx={{
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          height: 600,
          maxWidth: 400,
        }}
      >
        <AppBar elevation={0} position="sticky">
          <Toolbar
            sx={{
              mt: 1,
              justifyContent: 'center',
              display: 'flex',
              flexDirection: 'row',
            }}
            variant="dense"
          >
            <Typography variant="body1" fontWeight="bold">
              Offer Credentials
            </Typography>
            <Box sx={{ position: 'absolute', right: 0, pr: 2 }}>
              <IconButton onClick={onClose} size="small">
                <Close color="action" fontSize="small" />
              </IconButton>
            </Box>
          </Toolbar>
          <Tabs onChange={handleTabChange} variant="fullWidth" value={index}>
            <Tab sx={{ textTransform: 'capitalize' }} value={0} label="Free" />
            <Tab sx={{ textTransform: 'capitalize' }} value={1} label="Paid" />
          </Tabs>
          <Divider />
        </AppBar>

        <Box
          sx={{
            position: 'relative',
            overflowX: 'scroll',
            p: 2,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {showCommercialAgreementsWarning && <NoCommercialAgreement />}
          {commonFields.map((f) => (
            <TextField
              key={f.name}
              helperText={f.helperText}
              error={!!f.helperText}
              size="small"
              placeholder={f.name}
              sx={{ mb: 1 }}
              value={payload[f.name]}
              name={f.name}
              onChange={onFreeCredentialFormChange}
            />
          ))}

          <DynamicListInput<IClaim>
            title="Claims"
            list={claims}
            setList={setClaims}
            createNewItem={createNewClaim}
            renderItem={renderClaim}
          />
          {hasCommercialAgreements && index === 1 && (
            <PaidCredentialForm
              payload={payload}
              paidFields={paidFields}
              onChange={onPaidCredentialFormChange}
            />
          )}
        </Box>
        <Toolbar />
        <Box sx={{ width: '100%', p: 1, bottom: 0, position: 'absolute' }}>
          <AsyncButton
            disabled={disableSubmit}
            enableDefaultPadding
            onClick={onSubmit}
            color={isFree ? 'primary' : 'warning'}
            fullWidth
          >
            {buttonLabel}
          </AsyncButton>
        </Box>
      </Box>
    </Dialog>
  );
};
